import * as React from 'react';
import type { ResolveThunks } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm } from 'redux-form';

import type { FieldWorkClassificationCodeUpdateRM } from 'ab-requestModels/fieldWorkClassificationCode/upsert';

import type { RootState } from 'af-reducers';

import { FIELD_WORK_CLASSIFICATION_CODE_EDIT } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import type { BreadcrumbItem } from 'af-components/Breadcrumbs';
import Breadcrumbs from 'af-components/Breadcrumbs';

import * as FieldWorkClassificationCodeActions from 'af-actions/fieldWorkClassificationCode';

import FieldWorkClassificationCodeForm from '../Shared/Form';
import { validate } from '../Shared/Form/validation';

interface PathParams {
	id: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;

interface StateProps {
	companyName: string;
}

interface DispatchProps {
	update: typeof FieldWorkClassificationCodeActions.update;
	findById: typeof FieldWorkClassificationCodeActions.findById;
}

type FormOwnProps = OwnProps & StateProps & ResolveThunks<DispatchProps>;

type Props = FormOwnProps & InjectedFormProps<FieldWorkClassificationCodeUpdateRM, FormOwnProps>;

class FieldWorkClassificationCodeEdit extends React.PureComponent<Props> {

	static getBreadcrumbs = (orgAlias: string, companyName: string): BreadcrumbItem[] => {
		return [
			{ label: 'Classification Codes', url: CLIENT.COMPANY.RESOURCES.FIELD_WORK_CLASSIFICATION_CODE.LIST(orgAlias, companyName) },
			{ label: 'Edit Classification Code' },
		];
	};

	async componentDidMount() {
		const { initialize, findById, match: { params: { id } } } = this.props;
		const fieldWorkClassificationCode = await findById(+id);

		if (!fieldWorkClassificationCode) {
			throw new Error('Classification code not found');
		}

		const form: FieldWorkClassificationCodeUpdateRM = {
			id: fieldWorkClassificationCode.id,
			code: fieldWorkClassificationCode.code,
			description: fieldWorkClassificationCode.description,
		};
		initialize(form);
	}

	handleSubmit = async (form: FieldWorkClassificationCodeUpdateRM) => {
		const { history, update, location: { state: { orgAlias } }, companyName, match: { params: { id } } } = this.props;
		await update(+id, form);
		history.push(CLIENT.COMPANY.RESOURCES.FIELD_WORK_CLASSIFICATION_CODE.LIST(orgAlias, companyName));
	};

	render() {
		const { location: { state: { orgAlias } }, companyName } = this.props;

		return (
			<div className="form-segment">
				<Breadcrumbs items={FieldWorkClassificationCodeEdit.getBreadcrumbs(orgAlias, companyName)} />
				<FieldWorkClassificationCodeForm {...this.props} onSubmit={this.handleSubmit} />
			</div>
		);
	}
}

function mapStateToProps(state: RootState): StateProps {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps(): DispatchProps {
	return {
		update: FieldWorkClassificationCodeActions.update,
		findById: FieldWorkClassificationCodeActions.findById,
	};
}

const enhance = compose<React.ComponentClass<OwnProps>>(
	connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps()),
	reduxForm<FieldWorkClassificationCodeUpdateRM, FormOwnProps>({
		form: FIELD_WORK_CLASSIFICATION_CODE_EDIT,
		validate,
	})
);

export default enhance(FieldWorkClassificationCodeEdit);
